import { Routes, Route } from "react-router-dom";
import Login from "./components/Login";
import LoginProviders from "./components/LoginProviders";
import AppSkeleton from "./components/AppSkeleton";
import Layout from "./components/Layout";
import Admin from "./components/Admin";
import Missing from "./components/Missing";
import Unauthorized from "./components/Unauthorized";
import MfaGate from "./components/MfaGate";
import Onboarding from "./components/Onboarding";
import RequireAuth from "./components/RequireAuth";
import PersistLogin from "./components/PersistLogin";
import { InvoiceCenter } from "./components/InvoiceCenter";
import { CustomerOrganizationDetail } from "./components/CustomersTable/customerDetail";
import ReceivablesCenter from "./components/ReceivablesCenter";
import Analytics from "./components/Analytics";
import SettingsCenter from "./components/SettingsCenter";
import FilesIntegrationsPage from "./components/filesIntegrations/FilesIntegrationsPage";
import ArchiveCenter from "./components/InvoiceArchive";
import SupplierInvoices from "./components/SupplierInvoices";
import InvoicesForPayment from "./components/InvoicesForPayment";
import Dashboards from "./components/Dashboards/Dashboards";
import QuickBooksRedirect from "./components/QuickBooksRedirect";
import XeroRedirect from "./components/XeroRedirect";
import MicrosoftOAuthRedirect from "./components/MicrosoftOAuthRedirect";
import DraftValidationPage from "./components/DraftValidation/DraftValidationPage";
import TransactionsPage from "./components/transactions/TransactionsPage";
import CategoriesPage from "./components/CategoriesPage";
import { UserRole } from "./utils/types";
import { CashflowTabsLayout } from "./components/cashflow/CashflowTabsLayout";
import PricingPage from "./components/PricingPage";
import ForecastSettingsPage from "./components/forecast/ForecastSettingsPage";
import InviteAcceptPage from "./components/InviteAcceptPage";
import BankReconciliationPage from "./components/bankReconciliation/BankReconciliationPage";

const ROLES: Record<"User" | "Admin" | "Trial", UserRole> = {
  User: "USER",
  Admin: "ADMIN",
  Trial: "TRIAL",
};

function App() {
  return (
    <Routes>
      {/* The Layout component (within which all descendant components render, via an Outlet)
        can be used to apply styles across the app. */}
      <Route path="/" element={<Layout />}>
        {/* public routes */}
        <Route path="login" element={<Login />} />
        <Route path="login/providers" element={<LoginProviders />} />
        <Route
          path="microsoft_oauth_redirect"
          element={<MicrosoftOAuthRedirect />}
        />
        <Route path="invite/accept" element={<InviteAcceptPage />} />
        <Route path="mfa" element={<MfaGate />} />
        <Route path="unauthorized" element={<Unauthorized />} />

        {/* we want to protect these routes */}
        <Route element={<PersistLogin />}>
          <Route
            element={
              <RequireAuth
                allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                requireOrganization={false}
              />
            }
          >
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="/" element={<AppSkeleton />}>
              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["DASHBOARDS"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route index element={<Dashboards />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["CASHFLOW"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route path="cashflow" element={<CashflowTabsLayout />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["FORECAST"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route path="forecast" element={<ForecastSettingsPage />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["TRANSACTIONS"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route path="transactions" element={<TransactionsPage />} />
                <Route path="categories" element={<CategoriesPage />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["BANK_RECONCILIATION"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route
                  path="bank-reconciliation"
                  element={<BankReconciliationPage />}
                />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["RECEIVABLES"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route path="receivables" element={<ReceivablesCenter />} />
                <Route
                  path="customers/:customerId"
                  element={<CustomerOrganizationDetail />}
                />
                <Route path="invoice/:invoiceId" element={<InvoiceCenter />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["SETTINGS"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route path="settings" element={<SettingsCenter />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["FILES_INTEGRATIONS"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route
                  path="files-integrations"
                  element={<FilesIntegrationsPage />}
                />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["ANALYTICS"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route path="analytics" element={<Analytics />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["SUPPLIER_INVOICES"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route
                  path="supplier-invoices"
                  element={<SupplierInvoices />}
                />
                <Route
                  path="invoices-for-payment"
                  element={<InvoicesForPayment />}
                />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                    requireOrganization={true}
                    requiredScopes={["ARCHIVE"]}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route path="archive" element={<ArchiveCenter />} />
              </Route>

              <Route
                element={
                  <RequireAuth
                    allowedRoles={[ROLES.Admin]}
                    requireOrganization={false}
                    enforceLiveScopeCheck={true}
                  />
                }
              >
                <Route path="admin" element={<Admin />} />
                <Route
                  element={
                    <RequireAuth
                      allowedRoles={[ROLES.Admin]}
                      requireOrganization={false}
                      enforceLiveScopeCheck={true}
                    />
                  }
                >
                  <Route
                    path="admin/draft-validation"
                    element={<DraftValidationPage />}
                  />
                </Route>
              </Route>
            </Route>

            <Route
              element={
                <RequireAuth
                  allowedRoles={[ROLES.User, ROLES.Admin, ROLES.Trial]}
                  requireOrganization={true}
                  enforceLiveScopeCheck={true}
                />
              }
            >
              <Route
                path="quickbooks_oauth_redirect"
                element={<QuickBooksRedirect />}
              />
              <Route path="xero_oauth_redirect" element={<XeroRedirect />} />
            </Route>
          </Route>
        </Route>

        {/* catch all */}
        <Route path="*" element={<Missing />} />
      </Route>
    </Routes>
  );
}

export default App;
